import { ApiError, createApiClient } from '@neon-one/auctions-types';
import type { AxiosInstance } from 'axios';

import { env } from '@/shell/env';
import { getOrgSlug } from '@/shell/org';
import { attachCorrelationId, CORRELATION_ID_HEADER, logger } from '@/shell/observability';
import { useAdminSession } from '@/shell/stores/adminSession';
import { publishToast } from '@/shell/toast/bus';
import { markToastReported } from '@/shell/toast/reported';

let _client: AxiosInstance | null = null;

/**
 * Singleton admin HTTP client. The kernel envelope-unwrap interceptor
 * runs first; this layer adds RBAC-aware behavior on top:
 *
 *  - 401 → clear session + redirect to /login, EXCEPT ORG_NOT_PROVISIONED
 *    (a validly-authenticated supervisor sitting at the /supervisor lobby,
 *    not assumed into any org — not a dead session, see below)
 *  - 403 ROLE_INSUFFICIENT → toast + stay on current route
 *  - 403 ORG_SCOPE_VIOLATION → toast + redirect to /auctions
 *  - other 403 → generic toast
 *
 * Per ADR-007 §error-model. The toast bus is the only way an
 * interceptor can talk to the PrimeVue toast service from outside Vue
 * setup().
 */
export function httpClient(): AxiosInstance {
  if (_client) return _client;
  _client = createApiClient({
    baseUrl: env.apiBaseUrl,
    getJwt: () => useAdminSession().getJwt(),
  });
  _client.interceptors.request.use(config => {
    const slug = getOrgSlug();
    if (slug) config.headers['X-Org-Slug'] = slug;
    // Stamp a per-request correlation id so the backend (GT1-391) threads it
    // through its logs and echoes it back. Log method + path only — never
    // bodies, headers, or query values.
    const correlationId = attachCorrelationId(config);
    // Strip any inline query string / fragment so sensitive URL values
    // (tokens, emails) never reach the log. Log method + path only.
    const path = (config.url ?? '').split(/[?#]/, 1)[0];
    logger.debug('http_request', {
      correlationId,
      method: (config.method ?? 'get').toUpperCase(),
      path,
    });
    return config;
  });
  _client.interceptors.response.use(
    (resp) => {
      // Closes the loop on the http_request debug log above — without this,
      // the request side of a correlation id is logged but never its
      // outcome, so a request that hung or a slow endpoint can't be spotted
      // from client logs alone.
      logger.debug('http_response', {
        correlationId: resp.config.headers?.get?.(CORRELATION_ID_HEADER) as string | undefined,
        status: resp.status,
        path: (resp.config.url ?? '').split(/[?#]/, 1)[0],
      });
      return resp;
    },
    (err) => {
      if (err instanceof ApiError) {
        logger.debug('http_response', {
          correlationId: err.correlationId,
          status: err.status,
          code: err.code,
        });
        // A supervisor at the /supervisor lobby (not assumed into any org) has
        // a perfectly valid session — org-scoped endpoints 401 ORG_NOT_PROVISIONED
        // for them structurally, not because their session died. Callers on
        // this path (e.g. WaffleMenu's app list, adminSession's org-scoped
        // flag refresh on the WS feature_flag.changed broadcast) already
        // handle that failure gracefully; clearing the session and bouncing
        // to /login here would kill a healthy supervisor session and fight
        // that handling instead of leaving it to run.
        if (err.status === 401 && err.code !== 'ORG_NOT_PROVISIONED') {
          useAdminSession().clear();
          // A hard redirect (not a router.push) — this module sits underneath
          // the router (guards.ts already imports httpClient), so importing
          // the router instance here would be circular. Mirrors the
          // ORG_SCOPE_VIOLATION redirect below for the same reason.
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            const next = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.assign(`/login?error=session_expired&next=${next}`);
          }
        } else if (err.status === 403 && err.code === 'ROLE_INSUFFICIENT') {
          const required = err.body.requiredRole ?? 'admin';
          const current = err.body.currentRole ?? 'unknown';
          markToastReported(err);
          publishToast({
            severity: 'warn',
            summary: 'Permission denied',
            detail: `This action requires the ${required} role. You are signed in as ${current}.`,
          });
        } else if (err.status === 403 && err.code === 'ORG_SCOPE_VIOLATION') {
          markToastReported(err);
          publishToast({
            severity: 'danger',
            summary: 'Organization scope violation',
            detail: 'That resource does not belong to your organization.',
          });
          if (typeof window !== 'undefined') {
            window.location.assign('/auctions');
          }
        } else if (err.status === 403) {
          markToastReported(err);
          publishToast({
            severity: 'warn',
            summary: 'Permission denied',
            detail: 'You do not have access to that resource.',
          });
        }
      }
      throw err;
    },
  );
  return _client;
}
